/**
 * Secure Storage Utility
 * Provides encrypted wrapper around localStorage for secure client-side storage
 */

const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY || 'soma-secure-storage-key';

// Simple encryption using XOR + Base64 (sufficient for client-side obfuscation)
// Note: True security requires server-side sessions or HTTP-only cookies,
// but this prevents casual inspection of localStorage.
const encrypt = (text) => {
  if (!text) return text;
  try {
    const textToChars = (text) => text.split('').map((c) => c.charCodeAt(0));
    const byteHex = (n) => ("0" + Number(n).toString(16)).substr(-2);
    const applySaltToChar = (code) => textToChars(ENCRYPTION_KEY).reduce((a, b) => a ^ b, code);

    return text
      .split('')
      .map(textToChars)
      .map(applySaltToChar)
      .map(byteHex)
      .join('');
  } catch (e) {
    console.error('Encryption failed', e);
    return null;
  }
};

const decrypt = (encoded) => {
  if (!encoded) return encoded;
  try {
    const textToChars = (text) => text.split('').map((c) => c.charCodeAt(0));
    const applySaltToChar = (code) => textToChars(ENCRYPTION_KEY).reduce((a, b) => a ^ b, code);
    
    return encoded
      .match(/.{1,2}/g)
      .map((hex) => parseInt(hex, 16))
      .map(applySaltToChar)
      .map((charCode) => String.fromCharCode(charCode))
      .join('');
  } catch (e) {
    console.error('Decryption failed', e);
    return null;
  }
};

export const secureStorage = {
  getItem: (key) => {
    try {
      const value = localStorage.getItem(key);
      if (!value) return null;
      const decrypted = decrypt(value);
      return decrypted ? JSON.parse(decrypted) : null;
    } catch (e) {
      // Fallback for unencrypted data during migration
      try {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) : null;
      } catch {
        return localStorage.getItem(key);
      }
    }
  },

  setItem: (key, value) => {
    try {
      const stringValue = JSON.stringify(value);
      const encrypted = encrypt(stringValue);
      localStorage.setItem(key, encrypted);
    } catch (e) {
      console.error('Error saving to secure storage', e);
    }
  },

  removeItem: (key) => {
    localStorage.removeItem(key);
  },

  clear: () => {
    localStorage.clear();
  }
};

// Zustand middleware compatible storage
export const secureZustandStorage = {
  getItem: (name) => {
    const value = localStorage.getItem(name);
    if (!value) return null;
    try {
      const decrypted = decrypt(value);
      return decrypted ? JSON.parse(decrypted) : null;
    } catch (e) {
      // Fallback for unencrypted data
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
  },
  setItem: (name, value) => {
    const encrypted = encrypt(JSON.stringify(value));
    localStorage.setItem(name, encrypted);
  },
  removeItem: (name) => {
    localStorage.removeItem(name);
  },
};
